/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { YouTube } from "@material-ui/icons";

import {
  getCurrentLecture,
  getCurrentCourse,
  ApricotAction,
} from "client/actions/courses";
import { updateUser } from "client/actions/user";
import { getCurrentOwn, setOwnData } from "client/actions/own";
import { setScreenInfo } from "client/actions/modal";
import { Modal } from "components/Modal/Modal";

import { ScreenInfo } from "client/reducers/modalReducer";
import { AllOwns, OwnEntry } from "client/reducers/ownReducer";
import { selectorCurrentCourse } from "client/selectors/courseSelector";
import { selectorOwns, selectorCurrentOwn } from "client/selectors/ownSelector";

import {
  PlayerZone,
  LoadPlayerInfo,
  PlayerState,
  playerActivated,
} from "./PlayerZone";
import { NotesZone } from "./NotesZone";
import { SidePanel } from "./SidePanel";
import { InfoBoxMain } from "client/views/InfoBox/InfoBoxMain";
import { progressCircles } from "components/Form/FormField";
import { VideoPlayer } from "./VideoPlayer";
import { initScreen } from "client/views/MyStudy/studyUtils";
import requireCheck from "client/hocs/requireCheck";
import { EnrollDialog } from "client/views/CoursesPage/EnrollDialog";

import classroomStyle from "assets/jss/views/classroomPage/classroomStyle.jsx";
const styles = createStyles(classroomStyle as any);

export type ClsrmActionType = {
  action:
    | "back"
    | "prev"
    | "next"
    | "like"
    | "largeNZ"
    | "goLec"
    | "getLec"
    | "ibx"
    | "link"
    | "play"
    | "pause"
    | "play10"
    | "setCourse"
    | "setProgress"
    | "setPlayer"
    | "setPip";
  bool?: boolean;
  str?: string | null;
  num?: number;
  obj?: object | null;
};

// lecture info passed to PlayerZone (so it doesn't need to call getCurrentLecture)
export type LectureInfo = {
  title: string;
  body?: string;
  ybody?: string;
  completed?: boolean;
  length?: string;
  sched?: boolean;
  act?: boolean;
};

interface Props extends WithStyles<typeof styles> {
  getCurrentLecture: ApricotAction;
  getCurrentCourse: ApricotAction;
  getCurrentOwn: ApricotAction;
  updateUser: ApricotAction;
  setOwnData: ApricotAction;
  setScreenInfo: ApricotAction;
  currentCourse: any;
  currentOwn: OwnEntry;
  owns: AllOwns;
  user: any;
  screenInfo: ScreenInfo;
  playerState: PlayerState;
  match: any;
  history: any;
  location: any;
  classes: any;
}

const ClassroomPageBase: React.FC<Props> = ({
  getCurrentCourse,
  getCurrentLecture,
  getCurrentOwn,
  updateUser,
  setOwnData,
  setScreenInfo,
  currentCourse,
  currentOwn,
  owns,
  user,
  screenInfo,
  playerState,
  // component parameters
  match,
  history,
  location,
  classes,
}: Props) => {
  const [lecInfo, setLecInfo] = useState<LectureInfo>({ title: "" });
  const [playerTop, setPlayerTop] = useState("0px");
  const [playerWidth, setPlayerWidth] = useState("0px");
  const [portrait, setPortrait] = useState(false);

  const [largeNZ, setLargeNZ] = useState(false);
  const [selectedIBox, setSelectedIBox] = useState<string | null>(null);

  // --- Central controlled data to coordinate PlayerZone, InfoBoxMain...
  //
  // 1. Init data. Player needs init data to show the right UX (box or jwplayer) and set vPos.
  //    Since there're two instances of Player (normal & pip), the loadPlayer keeps this init data,
  //    to make sure the two instances switch seamlessly
  //    The loadPlayer is either prepared based on the launching info of Classroom, or get updated
  //    via onAction('setPip"...)
  const [loadPlayer, setLoadPlayer] = useState<LoadPlayerInfo>({
    load: false, // true: render JWPlayer; false: show PlayerBox
    vPos: 0, // when load is false, show vPos ? RESUME : START
    pip: false, // set state is async, so use this to identify if this loadPlayer is for normal or pip.
  });

  // 2. Running data. Keep the current state of Player required by child components of Classroom.
  //    a. player object, saved as a ref which is needed when unmounted
  //    b. pip state
  const refPlayer = useRef<VideoPlayer | null>(null);
  const [playerPip, setPlayerPip] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [showActivatePlayer, setShowActivatePlayer] = useState(false);

  const { cid, lid } = match.params;
  const refLecId = useRef(lid); // we need lid when unmounted

  const notesZoneId = "notes_zone";
  const playerZoneId = "player_zone";
  const mainColumnId = "main_Grid_id";

  useEffect(() => {
    // Things when mounting:
    // 1. Initialize screen rotate/change related listneer
    // 2. getCurrentCourse, getCurrentOwn (done when cid changed)

    const { screenEvt, screenEvtHandler } = initScreen(setScreenInfo);

    if (location.state) {
      // auto play requested, we need currentLecture right away
      getCurrentLecture(lid);
    }

    return () => {
      window.removeEventListener(screenEvt, screenEvtHandler);
      writeProgress(); // here we need the refLecId
    };
  }, []);

  useEffect(() => {
    // We don't put this in the 'mounted effect'. When user click a Ibox-LINK belongs to a different course,
    // the mounted effect won't be executed. So we need a different effect to handle the case where cid changes.
    getCurrentCourse(cid);
    getCurrentOwn(user ? user._id : null, cid);
  }, [cid]);

  useEffect(() => {
    refLecId.current = lid;
    if (currentCourse && currentCourse._id === cid) {
      const idx = currentCourse.lectures.findIndex(
        (lec: any) => lec._id === lid,
      );
      if (idx !== -1) {
        const {
          title,
          body,
          ybody,
          sched,
          act,
          length,
        } = currentCourse.lectures[idx];
        setLecInfo({ title, body, ybody, sched, act, length });
      }

      if (currentOwn && currentOwn.course === cid && currentOwn.lecData) {
        const vPos: number =
          (currentOwn.lecData[lid] && currentOwn.lecData[lid].videoPos) || 0;

        if (location.state) {
          // autoPlay requested (only available for course owner)
          if (location.state.autoPlay) {
            setLoadPlayer({ load: true, vPos, pip: false });
          }
        } else {
          // no autoPlay requested, but still setup vPos for PlayerBox Button
          setLoadPlayer({ load: false, vPos, pip: false });
        }
      }
    }
  }, [lid, currentCourse, currentOwn]);

  useEffect(() => {
    if (document.getElementById(notesZoneId)) {
      const deltaY = largeNZ ? getResizeLimit() : 0;
      const top =
        screenInfo.orientation === "landscape" && screenInfo.width < 1024
          ? "56.25%"
          : `calc(56.25% - ${deltaY}px)`;

      setPlayerTop(top);
    }
    setPortrait(screenInfo.orientation === "portrait");

    // reason to put currentCourse as a dependency: when currentCourse becomes available, it renders the page so
    // we can call getResizeLimit to calculate the player top
  }, [currentCourse, largeNZ, screenInfo.orientation]);

  useEffect(() => {
    const playerZone = document.getElementById(playerZoneId);

    if (playerZone) {
      const playerZoneStyle = getComputedStyle(playerZone, "");
      const width =
        largeNZ || selectedIBox
          ? Math.round((parseInt(playerZoneStyle.height || "") * 16) / 9)
          : parseInt(playerZoneStyle.width || "");

      setPlayerWidth(`${width}px`);
    }
  }, [playerTop]);

  const getResizeLimit = () => {
    const mainGrid = document.getElementById(mainColumnId);
    return mainGrid
      ? (parseInt(getComputedStyle(mainGrid, "").width || "") * 10) / 32
      : 0;
  };

  const imgPage = require("assets/img/desk3.jpg");

  const renderPlayerZone = () => {
    const completed =
      currentOwn && currentOwn.lecData && currentOwn.lecData[lid]
        ? currentOwn.lecData[lid].progress === 1023
        : false;

    return playerWidth ? (
      <PlayerZone
        onAction={(params: any) => onAction(params)}
        lecInfo={{ ...lecInfo, completed }}
        cid={cid}
        lid={lid}
        width={playerWidth}
        loadPlayer={loadPlayer}
        pip={playerPip}
        player={refPlayer.current}
      />
    ) : (
      <></>
    );
  };

  const notesZoneNode = () =>
    !lecInfo.sched && (
      <NotesZone
        cid={cid}
        lid={lid}
        owned={owns && owns[cid]}
        player={refPlayer.current}
        largeNZ={largeNZ}
        onAction={(params: any) => onAction(params)}
        // inform NotseZone if we're in portrait mode. Note that we cannot use (screenInfo.orientation === "portrait")
        // the reason is, without a state, we cannot trigger the redraw
        portrait={portrait}
      />
    );

  const writeProgress = () => {
    if (owns && owns[cid]) {
      const player = refPlayer.current;
      if (player) {
        const videoPos = Math.floor(player.getPosition());
        if (videoPos != loadPlayer.vPos)
          setOwnData(user._id, cid, refLecId.current, { videoPos });
      }
    }
  };

  const setProgress = (params: any) => {
    // 'progress' sets milestone per 10%, in bit op; e.g., 0x1101 (4th, 3rd, 1st bit is ON)
    // means video passed 40%, 30%, 10%
    const { obj, num } = params;
    if (num) {
      const lecData =
        (currentOwn && currentOwn.lecData && currentOwn.lecData[lid]) || {};
      var progressOrg = lecData.progress || 0;
      var progressNew = Math.pow(2, num - 1) | progressOrg;

      if (progressOrg !== progressNew) {
        // if it's a new milestone, add to update props
        obj.progress = progressNew;
      }
    }
    setOwnData(user._id, cid, refLecId.current, obj);
  };

  const onAction = (params: ClsrmActionType) => {
    switch (params.action) {
      case "setProgress":
        if (user && owns && owns[cid])
          setProgress({ obj: params.obj, num: params.num });
        break;

      case "setCourse":
        if (user && user.crs !== currentCourse._id) {
          updateUser(user._id, { crs: currentCourse._id }, false);
        }
        break;

      case "back":
        history.push(`/crp/${cid}`);
        break;

      case "prev":
      case "next":
        writeProgress();
        var idx = currentCourse.lectures.findIndex(
          (lec: any) => lec._id === lid,
        );
        params.action === "prev" ? idx-- : idx++;
        if (
          currentCourse.lectures[idx]._id != undefined &&
          !currentCourse.lectures[idx].sched
        ) {
          setSelectedIBox(null);
          history.push(`/clsrm/${cid}/${currentCourse.lectures[idx]._id}`);
        }
        break;

      case "like":
        setOwnData(user._id, cid, lid, { liked: params.bool });
        break;

      case "largeNZ":
        setLargeNZ(params.bool);
        break;

      case "goLec":
        writeProgress();
        history.push(`/clsrm/${cid}/${params.str}`);
        break;

      case "getLec":
        getCurrentLecture(params.str);
        break;

      case "ibx": // turn ON/OFF InfoBoxMain (str: ibx_id, null means turn off)
        // This setPlayerTop shrinks the PlayerZone to its min size. With fullscreen
        // InfoBoxMain, we don't need it anymore. (Keep for future reference.)
        //     setPlayerTop(`calc(56.25% - ${getResizeLimit()}px)`);

        if (!user || !(owns && owns[cid])) {
          // Either not logged-on or not owned, show EnrollDialog
          setShowEnroll(true);
        } else {
          setSelectedIBox(params.str || null);
        }
        break;

      case "link":
        {
          const link: string = params.str || "";
          link[0] === "/" ? history.push(link) : window.open(link);
        }
        break;

      case "setPlayer":
        if (params.obj) {
          const player = new VideoPlayer(params.str, params.obj); // type, player instance
          refPlayer.current = player;
        } else {
          refPlayer.current = null;
        }
        break;

      case "setPip": {
        const player = refPlayer.current;
        // Update the loadPlayer, so the new instance can continue seamlessly
        setLoadPlayer({
          load: player && player.isPlaying() ? true : false,
          vPos: player ? player.getPosition() : loadPlayer.vPos,
          pip: params.bool || false, // mark this loadPlayer as for normal or pip
        });
        refPlayer.current = null; // new instance will be created
        setPlayerPip(params.bool || false);
        break;
      }

      case "play": {
        const vPos = params.num;
        const player = refPlayer.current;
        if (player !== null) {
          if (vPos !== undefined) {
            // with vPos, tends to play it (instead of toggle play/pause), e.g., from Video bookmark
            if (!playerActivated(playerState)) {
              setShowActivatePlayer(true);
            } else {
              player.seek(vPos);
              if (playerState !== PlayerState.Playing) player.play();
            }
          } else {
            // w/o vPos, tends to toggle between play/pause, e.g., from InfoBoxMain where vPos is unknown
            player.play(); // play() is implemented as toggle
          }
        } else {
          // cases: 1) from v-bookmark with vPos; 2) from InfoBoxMain w/o vPos
          // set loadPlayer with load:true to start the JWPlayer
          setLoadPlayer({
            load: true,
            vPos: vPos === undefined ? loadPlayer.vPos : vPos,
            pip: playerPip,
          });
        }
        break;
      }

      case "pause": {
        const player = refPlayer.current;
        if (player && !lecInfo.act) player.pause();
        break;
      }

      case "play10": {
        const player = refPlayer.current;
        if (player) {
          var pos = player.getPosition();
          var len = player.getDuration();
          pos += params.num;
          if (pos > len) pos = len;
          if (pos < 0) pos = 0;
          player.seek(pos);
          break;
        }
      }
    }
  };

  const renderSidePanel = () => (
    <SidePanel
      onAction={(params: any) => onAction(params)}
      portrait={portrait}
      lecTitle={lecInfo.title}
      lid={lid}
      owned={owns && owns[cid]}
      lecData={currentOwn ? currentOwn.lecData : {}}
      notesZone={portrait ? notesZoneNode() : <></>}
    />
  );

  if (!currentCourse || currentCourse._id != cid) {
    // course is still loading
    return progressCircles();
  }

  return (
    <>
      {showEnroll && (
        <EnrollDialog
          owned={owns && owns[cid]}
          enroll={false}
          courses={[cid]}
          onExit={() => setShowEnroll(false)}
        />
      )}
      {showActivatePlayer && (
        <Modal
          title="Load Video First"
          buttons={[]}
          onExit={() => setShowActivatePlayer(false)}>
          To use the video bookmark, press the PLAY button&nbsp;
          <span style={{ display: "inline-block" }}>
            <YouTube
              style={{ position: "relative", top: "0.75rem" }}
              fontSize="large"
            />
          </span>
          &nbsp; on the Video Player to load the video first.
        </Modal>
      )}
      {selectedIBox && (
        <InfoBoxMain
          cid={cid}
          bid={selectedIBox}
          playerStarted={playerActivated(playerState)}
          playerPip={playerPip}
          onAction={(params: any) => onAction(params)}
        />
      )}
      {/* Keep rendering the PlayerZone (but not NotesZone), so the video can be played in the background
          We still render the SidePanel, so when return from InfoBoxMain, the Clipboard tab is still selected. */}
      <div
        className={classes.classroom}
        style={{
          background: "url(" + imgPage + ") fixed",
          backgroundSize: "cover",
        }}>
        <div className={classes.rootGrid}>
          <div className={classes.mainColumn} id={mainColumnId}>
            <div className={classes.mainGrid}>
              <div
                id={playerZoneId}
                className={classes.playerZone}
                style={{ paddingTop: playerTop }}>
                {renderPlayerZone()}
              </div>
              {!selectedIBox && (
                <div id={notesZoneId} className={classes.notesZone}>
                  {notesZoneNode()}
                </div>
              )}
            </div>
          </div>

          <div className={classes.sideColumn}>
            <div className={classes.sideGrid}>
              {/* Use a dummy div to create a space representing the header */}
              <div style={{ gridRow: "1 / 2" }} />
              {renderSidePanel()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    currentCourse: selectorCurrentCourse(state),
    owns: selectorOwns(state),
    currentOwn: selectorCurrentOwn(state),
    user: state.state_auth,
    screenInfo: state.state_modal.screenInfo,
    playerState: state.state_modal.playerState,
  };
}

function loadData(store: any, params: any) {
  // if (params.cid != "style.css" && params.cid != undefined) {
  // 'style.css' got passed as id for unknown reason...
  return store.dispatch(getCurrentCourse(params.cid));
}

const base: any = ClassroomPageBase;
export const ClassroomPage = {
  loadData,
  component: requireCheck(
    withStyles(styles)(
      connect(mapStateToProps, {
        getCurrentCourse,
        getCurrentLecture,
        getCurrentOwn,
        setOwnData,
        updateUser,
        setScreenInfo,
      })(base),
    ),
    // if logged in, make sure registered
    { loginRequired: false },
  ),
};

export const ClassroomPageTest = withStyles(styles)(ClassroomPageBase);

// The code for resizing zone by dragging the border
// ( dyResize is now a hooks state - deltaY )
// const resize = (e) => {
//   const dyResize = Math.min(
//     Math.max(this.state.dyResize + currentPosY - e.y, 0),
//     this.getResizeLimit(),
//   );
//
//   this.setState({ dyResize });
// };

// // Resume the add/remove event handler if we do resizing via dragging
// notesZone.addEventListener(
//   "mousedown", (e) => {
//     if (e.offsetY < BORDER_SIZE)
//       document.addEventListener("mousemove", resize, false);
//   },
//   false,
// );

// document.onmouseup = () => {
//   document.removeEventListener("mousemove", resize, false);
// };
